import WarehouseStock from './WarehouseStock';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { productsAPI, pricingAPI, wmsAPI } from '../utils/api';
import { Prodotto, PriceResolution, WmsConnectionStatus } from '../types';
import { useWmsStockBatch } from '../hooks/useWmsStock';
import { TagIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export interface ProductSelection {
  descrizione: string;
  prezzo_unitario: number;
  unita_misura: string;
  aliquota_iva?: number;
  prodotto_id?: string;
  pricing?: PriceResolution;
}

interface ProductAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onProductSelect: (selection: ProductSelection) => void;
  clienteId?: string;
  required?: boolean;
  className?: string;
}

const ProductAutocomplete: React.FC<ProductAutocompleteProps> = ({
  value,
  onChange,
  onProductSelect,
  clienteId,
  required,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Dropdown position (rendered in a portal with fixed positioning so it is
  // never clipped by overflow containers, e.g. the offer items table)
  const DROPDOWN_MAX_HEIGHT = 240;
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number } | null>(null);

  const updateDropdownPosition = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    const rect = input.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const openUp = spaceBelow < DROPDOWN_MAX_HEIGHT && rect.top > spaceBelow;
    setDropdownPos(
      openUp
        ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width }
        : { top: rect.bottom + 4, left: rect.left, width: rect.width }
    );
  }, []);

  const { data: productsResponse } = useQuery({
    queryKey: ['products-select'],
    queryFn: () => productsAPI.getProductsForSelect(true),
    staleTime: 5 * 60 * 1000,
  });

  const products: Prodotto[] = productsResponse?.data || [];

  // WMS stock integration
  const { data: wmsStatus } = useQuery<WmsConnectionStatus>({
    queryKey: ['wms-status'],
    queryFn: async () => {
      const response = await wmsAPI.getStatus();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const filteredIds = useMemo(() => {
    if (!wmsStatus?.is_active) return [];
    const q = value.toLowerCase();
    return value.length >= 1
      ? products
          .filter(p => p.nome.toLowerCase().includes(q) || (p.codice && p.codice.toLowerCase().includes(q)))
          .slice(0, 20)
          .map(p => p.id)
      : [];
  }, [products, value, wmsStatus?.is_active]);

  const { data: stockMap } = useWmsStockBatch(filteredIds);

  const filtered = value.length >= 1
    ? products.filter((p) => {
        const q = value.toLowerCase();
        return (
          p.nome.toLowerCase().includes(q) ||
          (p.codice && p.codice.toLowerCase().includes(q))
        );
      })
    : [];

  const showDropdown = isOpen && filtered.length > 0;

  useEffect(() => {
    setHighlightIndex(-1);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keep the portal dropdown anchored to the input while open
  useEffect(() => {
    if (!showDropdown) return;
    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [showDropdown, updateDropdownPosition]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  const selectProduct = async (product: Prodotto) => {
    const desc = product.codice
      ? `[${product.codice}] ${product.nome}`
      : product.nome;

    let pricing: PriceResolution | undefined;
    let finalPrice = Number(product.prezzo_unitario);

    // If clienteId is set, resolve pricing
    if (clienteId) {
      try {
        const response = await pricingAPI.resolvePrice(product.id, clienteId);
        pricing = response.data;
        finalPrice = Number(pricing.prezzo_finale);
      } catch {
        // Fallback to product base price
      }
    }

    onProductSelect({
      descrizione: desc,
      prezzo_unitario: finalPrice,
      unita_misura: product.unita_misura,
      aliquota_iva: product.aliquota_iva != null ? Number(product.aliquota_iva) : undefined,
      prodotto_id: product.id,
      pricing,
    });
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      selectProduct(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        required={required}
        className={className || 'block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500'}
        placeholder="Cerca prodotto o digita descrizione..."
        autoComplete="off"
      />
      {showDropdown && dropdownPos && createPortal(
        <ul
          ref={listRef}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            bottom: dropdownPos.bottom,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
          className="z-[70] max-h-60 overflow-auto rounded-md bg-white border border-gray-200 shadow-lg"
        >
          {filtered.slice(0, 20).map((product, idx) => (
            <li
              key={product.id}
              onMouseDown={() => selectProduct(product)}
              onMouseEnter={() => setHighlightIndex(idx)}
              className={`cursor-pointer px-3 py-2 text-sm ${
                idx === highlightIndex ? 'bg-primary-50 text-primary-900' : 'text-gray-900 hover:bg-gray-50'
              }`}
            >
              <div className="flex justify-between items-center">
                <span>
                  {product.codice && (
                    <span className="font-mono text-xs text-gray-500 mr-2">[{product.codice}]</span>
                  )}
                  {product.nome}
                </span>
                <span className="text-gray-500 ml-2 whitespace-nowrap flex items-center gap-1">
                  {(() => {
                    const stock = stockMap?.get(product.id);
                    if (stock?.wms_available) {
                      const qty = stock.available_quantity ?? 0;
                      if (qty <= 0) {
                        return <ExclamationTriangleIcon className="h-3 w-3 text-red-500" title="Esaurito" />;
                      }
                      return <span className="text-xs text-green-600">{qty} pz</span>;
                    }
                    return null;
                  })()}
                  {clienteId && (
                    <TagIcon className="h-3 w-3 text-green-500" title="Prezzo personalizzato disponibile" />
                  )}
                  <span className="text-gray-400">|</span>
                  {Number(product.prezzo_unitario).toFixed(2)} / {product.unita_misura}
                </span>
              </div>
              {stockMap?.get(product.id)?.wms_available && (
                <WarehouseStock stock={stockMap.get(product.id)!} unit={product.unita_misura || 'pz'} />
              )}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default ProductAutocomplete;
